import React, { useState } from 'react';
import { Modal, Box, TextField, Typography, Button, Grid, IconButton, Tooltip, MenuItem } from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { createEntity } from "../../../api's/DataModelApi";

const dataTypeOptions = ['VARCHAR', 'INTEGER', 'TIMESTAMP', 'BOOLEAN', 'DATE', 'FLOAT', 'TEXT'];
const boolOptions = [true, false];

const AddEntity = ({ open, onSuccess, onClose, engagementId }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState({
    entityName: '',
    description: '',
    primaryColumn: '',
    attributes: [
      {
        attribute_name: '',
        attribute_description: '',
        data_type: 'VARCHAR',
        required_col: true,
        unique_col: false,
      },
    ],
  });

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAttrChange = (index, field, value) => {
    const updated = [...formData.attributes];
    updated[index][field] = value;
    setFormData((prev) => ({ ...prev, attributes: updated }));
  };

  const addAttribute = () => {
    setFormData((prev) => ({
      ...prev,
      attributes: [
        ...prev.attributes,
        {
          attribute_name: '',
          attribute_description: '',
          data_type: 'VARCHAR',
          required_col: true,
          unique_col: false,
        },
      ],
    }));
  };

  const removeAttribute = (index) => {
    const updated = [...formData.attributes];
    updated.splice(index, 1);
    setFormData((prev) => ({ ...prev, attributes: updated }));
  };

  const validateForm = () => {
    if (!formData.entityName.trim()) {
      enqueueSnackbar('Entity name is required', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      return false;
    }

    if (!formData.description.trim()) {
      enqueueSnackbar('Description is required', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      return false;
    }

    if (formData.attributes.length === 0) {
      enqueueSnackbar('At least one attribute is required', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      return false;
    }

    const hasInvalidAttribute = formData.attributes.some(
      (attr) => !attr.attribute_name.trim() || !attr.attribute_description.trim()
    );

    if (hasInvalidAttribute) {
      enqueueSnackbar('Each attribute must have name and description', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      return false;
    }

    if (!formData.primaryColumn.trim()) {
      enqueueSnackbar('Primary column must be selected', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      const payload = {
        name: formData.entityName.trim(),
        description: formData.description.trim(),
        primaryKey: formData.primaryColumn,
        attributes: formData.attributes.map((attr) => ({
          name: attr.attribute_name.trim(),
          description: attr.attribute_description.trim(),
          data_type: attr.data_type,
          primary_col: attr.attribute_name.trim() === formData.primaryColumn,
          required_col: attr.required_col,
          unique_col: attr.unique_col,
        })),
      };

      console.log('Add Entity Payload:', payload);

      const response = await createEntity(engagementId, payload);

      if (response?.status === 201 || response?.status === 200) {
        enqueueSnackbar('Entity created successfully!', {
          variant: 'success',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
        if (onSuccess) onSuccess();
        onClose();
      } else {
        enqueueSnackbar('Failed to create entity', {
          variant: 'error',
          autoHideDuration: 3000,
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        });
      }
    } catch (err) {
      console.error('Error creating entity:', err);
      enqueueSnackbar('Error occurred while creating entity', {
        variant: 'error',
        autoHideDuration: 3000,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 2,
          p: 4,
          width: 850,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Add New Entity
        </Typography>

        {/* ENTITY INFO */}
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Entity Name"
              variant="outlined"
              size="small"
              value={formData.entityName}
              onChange={(e) => handleChange('entityName', e.target.value)}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Description"
              variant="outlined"
              size="small"
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
            />
          </Grid>
        </Grid>

        {/* ATTRIBUTES SECTION */}
        <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>
          Attributes
        </Typography>

        {formData.attributes.map((attr, index) => (
          <Box key={index} sx={{ p: 1, background: '#fafafa', borderRadius: 1, mb: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={2.5}>
                <TextField
                  fullWidth
                  label="Name"
                  size="small"
                  value={attr.attribute_name}
                  onChange={(e) => handleAttrChange(index, 'attribute_name', e.target.value)}
                />
              </Grid>
              <Grid item xs={3}>
                <TextField
                  fullWidth
                  label="Description"
                  size="small"
                  value={attr.attribute_description}
                  onChange={(e) => handleAttrChange(index, 'attribute_description', e.target.value)}
                />
              </Grid>
              <Grid item xs={2}>
                <TextField
                  fullWidth
                  select
                  label="Data Type"
                  size="small"
                  value={attr.data_type}
                  onChange={(e) => handleAttrChange(index, 'data_type', e.target.value)}
                >
                  {dataTypeOptions.map((dt) => (
                    <MenuItem key={dt} value={dt}>
                      {dt}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={2}>
                <TextField
                  fullWidth
                  select
                  label="Required"
                  size="small"
                  value={attr.required_col}
                  onChange={(e) => handleAttrChange(index, 'required_col', e.target.value === 'true')}
                >
                  {boolOptions.map((opt) => (
                    <MenuItem key={opt} value={opt.toString()}>
                      {opt.toString()}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={2}>
                <TextField
                  fullWidth
                  select
                  label="Unique"
                  size="small"
                  value={attr.unique_col}
                  onChange={(e) => handleAttrChange(index, 'unique_col', e.target.value === 'true')}
                >
                  {boolOptions.map((opt) => (
                    <MenuItem key={opt} value={opt.toString()}>
                      {opt.toString()}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={0.5}>
                <Tooltip title="Remove Attribute">
                  <IconButton color="error" onClick={() => removeAttribute(index)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>
          </Box>
        ))}

        <Button startIcon={<Add />} onClick={addAttribute} sx={{ mb: 2, textTransform: 'none' }}>
          Add Attribute
        </Button>

        {/* PRIMARY COLUMN DROPDOWN */}
        {formData.attributes.length > 0 && (
          <TextField
            select
            fullWidth
            label="Primary Column"
            size="small"
            value={formData.primaryColumn}
            onChange={(e) => handleChange('primaryColumn', e.target.value)}
            sx={{ mb: 3 }}
          >
            {formData.attributes
              .filter((a) => a.attribute_name.trim() !== '')
              .map((a, idx) => (
                <MenuItem key={idx} value={a.attribute_name}>
                  {a.attribute_name}
                </MenuItem>
              ))}
          </TextField>
        )}

        {/* ACTION BUTTONS */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit}>
            Save
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default AddEntity;
